import { useGenericCRUD } from '@/hooks/generic/useGenericCRUD';

export interface Company {
  id: string;
  user_id: string;
  name: string;
  industry?: string;
  website?: string;
  phone?: string;
  email?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  company_type: 'client' | 'prospect' | 'vendor' | 'contractor' | 'partner';
  status: 'active' | 'inactive' | 'prospect' | 'archived';
  annual_revenue?: number;
  employee_count?: number;
  founded_year?: number;
  relationship_tier?: 'vip' | 'standard' | 'low_priority';
  account_manager_id?: string;
  notes?: string;
  tags?: string[];
  // New contractor/vendor fields
  trade_specialties?: string[];
  service_area_states?: string[];
  service_area_radius_miles?: number;
  license_number?: string;
  license_state?: string;
  license_expiry_date?: string;
  license_type?: string;
  insurance_general_liability_amount?: number;
  insurance_workers_comp_amount?: number;
  insurance_umbrella_amount?: number;
  insurance_expiry_date?: string;
  insurance_carrier?: string;
  bond_capacity?: number;
  bonding_company?: string;
  is_mwbe_certified?: boolean;
  certifications?: string[];
  duns_number?: string;
  federal_tax_id?: string;
  union_status?: 'union' | 'non_union' | 'merit_shop' | 'mixed';
  payment_terms?: string;
  typical_crew_size?: number;
  years_in_business?: number;
  prequalification_status?: 'not_started' | 'in_progress' | 'pending_review' | 'approved' | 'rejected' | 'expired';
  prequalification_date?: string;
  prequalification_expiry_date?: string;
  prequalification_notes?: string;
  safety_rating?: number;
  safety_rating_notes?: string;
  quality_rating?: number;
  quality_rating_notes?: string;
  reliability_rating?: number;
  reliability_rating_notes?: string;
  overall_rating?: number;
  last_project_date?: string;
  contractor_notes?: string;
  created_at: string;
  updated_at: string;
}

export interface CompanyFormData {
  name: string;
  industry?: string;
  website?: string;
  phone?: string;
  email?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  company_type: 'client' | 'prospect' | 'vendor' | 'contractor' | 'partner';
  status: 'active' | 'inactive' | 'prospect' | 'archived';
  annual_revenue?: number;
  employee_count?: number;
  founded_year?: number;
  relationship_tier?: 'vip' | 'standard' | 'low_priority';
  notes?: string;
  tags?: string[];
  // New contractor/vendor fields
  trade_specialties?: string[];
  service_area_states?: string[];
  service_area_radius_miles?: number;
  license_number?: string;
  license_state?: string;
  license_expiry_date?: string;
  license_type?: string;
  insurance_general_liability_amount?: number;
  insurance_workers_comp_amount?: number;
  insurance_umbrella_amount?: number;
  insurance_expiry_date?: string;
  insurance_carrier?: string;
  bond_capacity?: number;
  bonding_company?: string;
  is_mwbe_certified?: boolean;
  certifications?: string[];
  duns_number?: string;
  federal_tax_id?: string;
  union_status?: 'union' | 'non_union' | 'merit_shop' | 'mixed';
  payment_terms?: string;
  typical_crew_size?: number;
  years_in_business?: number;
  prequalification_date?: string;
  prequalification_expiry_date?: string;
  last_project_date?: string;
  contractor_notes?: string;
}

export function useCompanies() {
  const genericCRUD = useGenericCRUD<Company, CompanyFormData>({
    tableName: 'companies',
    queryKey: 'companies',
    orderBy: { column: 'name', ascending: true },
    dependencies: ['contacts', 'accountPlans'],
    toastMessages: {
      create: 'Company created successfully',
      update: 'Company updated successfully',
      delete: 'Company deleted successfully',
    },
  });

  return {
    companies: genericCRUD.data,
    loading: genericCRUD.isLoading,
    createCompany: genericCRUD.createMutation.mutateAsync,
    updateCompany: ({ id, data }: { id: string; data: Partial<CompanyFormData> }) => 
      genericCRUD.updateMutation.mutateAsync({ id, data }),
    deleteCompany: genericCRUD.deleteMutation.mutateAsync,
  };
}
